// Shared real-time WebSocket client (singleton).
// Mirrors the contract in /app/memory/SHARED_API_CONTRACT.md so the mobile
// client can implement the same behaviour against the same backend.
import { getToken } from "@/lib/api";

const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws") + "/api/ws";

class RealtimeClient {
  constructor() {
    this.ws = null;
    this.handlers = {};        // type -> Set(fn)
    this.shows = new Set();    // rooms to (re)subscribe on connect
    this.shouldRun = false;
    this.retry = 0;
    this.pingTimer = null;
  }

  connect() {
    const token = getToken();
    if (!token) return;
    this.shouldRun = true;
    if (this.ws && (this.ws.readyState === 0 || this.ws.readyState === 1)) return;
    this.ws = new WebSocket(`${WS_BASE}?token=${encodeURIComponent(token)}`);

    this.ws.onopen = () => {
      this.retry = 0;
      this.shows.forEach((s) => this._send({ type: "subscribe", show: s }));
      this.pingTimer = setInterval(() => this._send({ type: "ping" }), 25000);
    };
    this.ws.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }
      (this.handlers[msg.type] || []).forEach((fn) => { try { fn(msg); } catch { /* noop */ } });
      (this.handlers["*"] || []).forEach((fn) => { try { fn(msg); } catch { /* noop */ } });
    };
    this.ws.onclose = () => {
      clearInterval(this.pingTimer);
      this.ws = null;
      if (this.shouldRun) {
        this.retry = Math.min(this.retry + 1, 6);
        setTimeout(() => this.connect(), 500 * 2 ** this.retry);
      }
    };
    this.ws.onerror = () => { try { this.ws.close(); } catch { /* noop */ } };
  }

  disconnect() {
    this.shouldRun = false;
    clearInterval(this.pingTimer);
    if (this.ws) { try { this.ws.close(); } catch { /* noop */ } this.ws = null; }
    this.shows.clear();
  }

  _send(obj) {
    if (this.ws && this.ws.readyState === 1) this.ws.send(JSON.stringify(obj));
  }

  subscribeShow(showId) {
    this.shows.add(showId);
    this._send({ type: "subscribe", show: showId });
  }
  unsubscribeShow(showId) {
    this.shows.delete(showId);
    this._send({ type: "unsubscribe", show: showId });
  }

  on(type, fn) {
    (this.handlers[type] = this.handlers[type] || new Set()).add(fn);
    return () => this.handlers[type] && this.handlers[type].delete(fn);
  }
}

export const realtime = new RealtimeClient();
